/**
 * Compliance Repository Implementation
 * Implements the ComplianceRepository interface
 * Coordinates between API calls and mappers
 */

import { ComplianceRepository } from "../../domain/repositories/ComplianceRepository";
import {
  ComplianceTypeEntity,
  MasterComplianceCalendarEntity,
  GroupedCalendarEntries,
} from "../../domain/entities";
import {
  getComplianceTypes,
  getComplianceTypeById,
  createComplianceType,
  updateComplianceType,
  deleteComplianceType,
  getCalendarEntries,
  getCalendarEntriesGrouped,
  getCalendarEntryById,
  createCalendarEntry,
  updateCalendarEntry,
  deleteCalendarEntry,
} from "../api/complianceApi";
import { ComplianceMapper } from "../mappers/ComplianceMapper";

export class ComplianceRepositoryImpl implements ComplianceRepository {
  // Compliance Types
  async getComplianceTypes(): Promise<ComplianceTypeEntity[]> {
    const response = await getComplianceTypes();
    if (response.success && response.data) {
      return ComplianceMapper.toComplianceTypeEntityArray(response.data);
    }
    throw new Error("Failed to fetch compliance types");
  }

  async getComplianceTypeById(id: number): Promise<ComplianceTypeEntity> {
    const response = await getComplianceTypeById(id);
    if (response.success && response.data) {
      return ComplianceMapper.toComplianceTypeEntity(response.data);
    }
    throw new Error("Compliance type not found");
  }

  async createComplianceType(data: Partial<ComplianceTypeEntity>): Promise<ComplianceTypeEntity> {
    const response = await createComplianceType(data);
    if (response.success && response.data) {
      return ComplianceMapper.toComplianceTypeEntity(response.data);
    }
    throw new Error("Failed to create compliance type");
  }

  async updateComplianceType(
    id: number,
    data: Partial<ComplianceTypeEntity>
  ): Promise<ComplianceTypeEntity> {
    const response = await updateComplianceType(id, data);
    if (response.success && response.data) {
      return ComplianceMapper.toComplianceTypeEntity(response.data);
    }
    throw new Error("Failed to update compliance type");
  }

  async deleteComplianceType(id: number): Promise<void> {
    await deleteComplianceType(id);
  }

  // Master Compliance Calendar
  async getCalendarEntries(filters?: {
    compliance_type_id?: number;
    period?: string;
    is_active?: boolean;
  }): Promise<MasterComplianceCalendarEntity[]> {
    const response = await getCalendarEntries(filters);
    if (response.success && response.data) {
      return ComplianceMapper.toMasterCalendarEntityArray(response.data);
    }
    throw new Error("Failed to fetch calendar entries");
  }

  async getCalendarEntriesGrouped(): Promise<GroupedCalendarEntries> {
    const response = await getCalendarEntriesGrouped();
    if (response.success && response.data) {
      return ComplianceMapper.toGroupedCalendarEntries(response);
    }
    throw new Error("Failed to fetch grouped calendar entries");
  }

  async getCalendarEntryById(id: number): Promise<MasterComplianceCalendarEntity> {
    const response = await getCalendarEntryById(id);
    if (response.success && response.data) {
      return ComplianceMapper.toMasterCalendarEntity(response.data);
    }
    throw new Error("Calendar entry not found");
  }

  async createCalendarEntry(
    data: Partial<MasterComplianceCalendarEntity>
  ): Promise<MasterComplianceCalendarEntity> {
    const response = await createCalendarEntry(data);
    if (response.success && response.data) {
      return ComplianceMapper.toMasterCalendarEntity(response.data);
    }
    throw new Error("Failed to create calendar entry");
  }

  async updateCalendarEntry(
    id: number,
    data: Partial<MasterComplianceCalendarEntity>
  ): Promise<MasterComplianceCalendarEntity> {
    const response = await updateCalendarEntry(id, data);
    if (response.success && response.data) {
      return ComplianceMapper.toMasterCalendarEntity(response.data);
    }
    throw new Error("Failed to update calendar entry");
  }

  async deleteCalendarEntry(id: number): Promise<void> {
    await deleteCalendarEntry(id);
  }
}

